import { useLayoutEffect, useRef, useState } from 'react'
import { mfLogoBaseUrl } from '../../../../../../../env'
import { trackEvent } from '../../../../../../../utils/appBridge'
import { EventNames } from '../../../../../../../types/misc.types'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'

const PLAN_TAB_WIDTH_PX = 64
const PLAN_TAB_GAP_PX = 16
const PLAN_TAB_ROW_END_PADDING_PX = 40
const HORIZONTAL_DRAG_THRESHOLD_PX = 8

type PlanUnderlineTabsProps = {
  funds: {
    fundName: string
    fundIsin?: string
    mfLogoUrl: string
  }[]
  activeTabIndex: number
  onChange: (activeTabIndex: number) => void
  analyticsScreen?: string
}

export function PlanUnderlineTabs({
  funds,
  activeTabIndex,
  onChange,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: PlanUnderlineTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const translateXRef = useRef(0)
  const didDragHorizontallyRef = useRef(false)
  const [translateX, setTranslateX] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const rowWidth =
    funds.length * PLAN_TAB_WIDTH_PX +
    Math.max(funds.length - 1, 0) * PLAN_TAB_GAP_PX +
    PLAN_TAB_ROW_END_PADDING_PX
  const isHorizontallyScrollable = containerWidth > 0 && rowWidth > containerWidth

  const applyTranslateX = (nextTranslateX: number) => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const clampedTranslateX = clampTabRowTranslateX(
      nextTranslateX,
      container.clientWidth,
      rowWidth
    )
    translateXRef.current = clampedTranslateX
    setTranslateX(clampedTranslateX)
  }

  useLayoutEffect(() => {
    const container = containerRef.current
    const activeTabElement = tabRefs.current[activeTabIndex]
    if (!container || !activeTabElement) {
      return
    }

    const centerActiveTab = () => {
      const activeTab = tabRefs.current[activeTabIndex]
      if (!container || !activeTab) {
        return
      }

      setContainerWidth(container.clientWidth)
      applyTranslateX(
        getCenteredTabRowTranslateX({
          containerWidth: container.clientWidth,
          activeTabOffsetLeft: activeTab.offsetLeft,
          activeTabWidth: activeTab.offsetWidth,
          rowWidth
        })
      )
    }

    centerActiveTab()

    const resizeObserver = new ResizeObserver(centerActiveTab)
    resizeObserver.observe(container)
    return () => {
      resizeObserver.disconnect()
    }
  }, [activeTabIndex, rowWidth])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || !isHorizontallyScrollable) {
      return undefined
    }

    const dragState = {
      isTracking: false,
      isHorizontalDrag: false,
      startPointerX: 0,
      startPointerY: 0,
      startTranslateX: 0
    }

    const resetDragState = () => {
      dragState.isTracking = false
      dragState.isHorizontalDrag = false
      setIsDragging(false)
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return
      }

      dragState.isTracking = true
      dragState.isHorizontalDrag = false
      dragState.startPointerX = event.clientX
      dragState.startPointerY = event.clientY
      dragState.startTranslateX = translateXRef.current
      didDragHorizontallyRef.current = false
      setIsDragging(true)
      container.setPointerCapture(event.pointerId)
    }

    const onPointerMove = (event: PointerEvent) => {
      if (!dragState.isTracking) {
        return
      }

      const deltaX = event.clientX - dragState.startPointerX
      const deltaY = event.clientY - dragState.startPointerY

      if (!dragState.isHorizontalDrag) {
        if (
          Math.abs(deltaX) < HORIZONTAL_DRAG_THRESHOLD_PX &&
          Math.abs(deltaY) < HORIZONTAL_DRAG_THRESHOLD_PX
        ) {
          return
        }

        dragState.isHorizontalDrag = Math.abs(deltaX) > Math.abs(deltaY)
        if (!dragState.isHorizontalDrag) {
          resetDragState()
          return
        }
      }

      event.preventDefault()
      didDragHorizontallyRef.current = true
      applyTranslateX(dragState.startTranslateX + deltaX)
    }

    const onPointerEnd = (event: PointerEvent) => {
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId)
      }
      resetDragState()
    }

    container.addEventListener('pointerdown', onPointerDown)
    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerup', onPointerEnd)
    container.addEventListener('pointercancel', onPointerEnd)

    return () => {
      container.removeEventListener('pointerdown', onPointerDown)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerup', onPointerEnd)
      container.removeEventListener('pointercancel', onPointerEnd)
    }
  }, [isHorizontallyScrollable, rowWidth])

  if (funds.length <= 1) {
    return null
  }

  const safeActiveIndex = activeTabIndex < 0 ? 0 : activeTabIndex
  const indicatorLeftPx = safeActiveIndex * (PLAN_TAB_WIDTH_PX + PLAN_TAB_GAP_PX)

  return (
    <div
      ref={containerRef}
      className="h-10 overflow-hidden border-b border-[#E5E9EC] !px-5 !h-[80px]"
      style={{ touchAction: 'pan-y' }}
    >
      <div
        className={`relative flex h-full w-max min-w-full pr-5 ${isDragging ? '' : 'transition-transform duration-200 ease-out'}`}
        style={{
          transform: `translateX(${translateX}px)`,
          columnGap: `${PLAN_TAB_GAP_PX}px`
        }}
      >
        {funds.map((fund, counterFundIndex) => {
          const isActive = counterFundIndex === activeTabIndex
          return (
            <div
              key={String(counterFundIndex)}
              ref={(element) => {
                tabRefs.current[counterFundIndex] = element
              }}
              className="flex shrink-0 cursor-pointer flex-col items-center justify-end"
              style={{ width: `${PLAN_TAB_WIDTH_PX}px` }}
              onClick={() => {
                if (didDragHorizontallyRef.current) {
                  didDragHorizontallyRef.current = false
                  return
                }
                if (
                  !Number.isInteger(counterFundIndex) ||
                  counterFundIndex < 0 ||
                  counterFundIndex >= funds.length
                ) {
                  return
                }
                trackEvent({
                  eventName: EventNames.UserClicked,
                  payload: {
                    ScreenName: analyticsScreen,
                    eventId: PlansLumpsumSheetEventIds.PairTab,
                    buttonLabel: fund.fundName,
                    ...(fund.fundName ? { fundName: fund.fundName } : {}),
                    ...(fund.fundIsin ? { fundIsin: fund.fundIsin } : {})
                  }
                })
                onChange(counterFundIndex)
              }}
            >
              <PlanRecommendationTabLabel
                isActive={isActive}
                label={fund.fundName}
                mfLogoUrl={fund.mfLogoUrl}
              />
            </div>
          )
        })}
        <div
          className="absolute bottom-0 h-[2px] bg-[#232323] transition-[left] duration-200 ease-out"
          style={{ left: `${indicatorLeftPx}px`, width: `${PLAN_TAB_WIDTH_PX}px` }}
        />
      </div>
    </div>
  )
}

function getCenteredTabRowTranslateX({
  containerWidth,
  activeTabOffsetLeft,
  activeTabWidth,
  rowWidth
}: {
  containerWidth: number
  activeTabOffsetLeft: number
  activeTabWidth: number
  rowWidth: number
}): number {
  const tabCenter = activeTabOffsetLeft + activeTabWidth / 2
  const centeredTranslateX = containerWidth / 2 - tabCenter
  return clampTabRowTranslateX(centeredTranslateX, containerWidth, rowWidth)
}

function clampTabRowTranslateX(
  translateX: number,
  containerWidth: number,
  rowWidth: number
): number {
  const minTranslateX = Math.min(0, containerWidth - rowWidth)
  return Math.max(minTranslateX, Math.min(0, translateX))
}

export function PlanRecommendationTabLabel({
  label,
  mfLogoUrl,
  isActive
}: {
  label: string
  mfLogoUrl: string
  isActive: boolean
}) {
  const logoSrc = mfLogoUrl.length > 0 ? `${mfLogoBaseUrl}${mfLogoUrl}` : ''

  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-end pb-2 
        ${isActive ? 'text-[#232323] font-[500]' : 'text-[#6A6E6E] font-[400]'}`}
    >
      <div className="h-10 w-10 shrink-0">
        {logoSrc ? (
          <img className="h-full w-full object-contain" src={logoSrc} alt="" />
        ) : null}
      </div>
      <span
        className={`w-full text-center text-[11px] leading-[130%] line-clamp-2 break-words whitespace-normal ${isActive ? 'text-[#232323]' : 'text-[#6A6E6E]'}`}
      >
        {label}
      </span>
    </div>
  )
}
